import {ChangeDetectionStrategy, Component, computed, Injectable, input, linkedSignal, output, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {TranslateModule} from '@ngx-translate/core';
import {GruppeMedlemListeModell, GruppeMedlemModell, GruppeMedlemRessursClient, Invitasjonsstatus, MedlemId, Medlemstatus} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {muligeTilgangsrollerForMedlem} from 'felles-ng2/modell/medlemModellHjelpere';
import {EnumOversettelser} from 'felles-ng2/tjenester/i18n/EnumOversettelser';
import {FormaterDatoPeriodeUtenTidPipe} from 'felles-ng2/pipes/FormaterDatoPeriodeUtenTidPipe';
import {AvatarKomponent} from 'web-ng2/delt/komponenter/avatar/avatar';
import {MedlemGjenåpneTilgangDialogKomponent} from 'web-ng2/moduler/medlem/modale/medlem-gjenåpnetilgang-dialog/medlem-gjenåpnetilgang-dialog';
import {MedlemStengTilgangDialogKomponent} from 'web-ng2/moduler/medlem/modale/medlem-stengtilgang-dialog/medlem-stengtilgang-dialog';
import {firstValueFrom} from 'rxjs';
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";

type Medlem = GruppeMedlemListeModell | GruppeMedlemModell;

@Component({
    selector: 'administrasjon-medlem',
    templateUrl: 'administrasjon-medlem.html',
    styleUrl: 'administrasjon-medlem.scss',
    imports: [AvatarKomponent, FormaterDatoPeriodeUtenTidPipe, MatButton, TranslateModule, TittelVerdiKomponent, TittelVerdiSamlingKomponent, VerdiKomponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonMedlemKomponent {
    static ajsSelector = 'administrasjonMedlem';

    medlem = input.required<Medlem>();
    tilgangEndretFor = output<GruppeMedlemModell>();

    readonly viserDetaljer = signal(false);
    readonly visningsmedlem = linkedSignal(() => this.medlem());
    readonly gruppe = linkedSignal(() => gruppeForMedlem(this.medlem()));
    readonly tilgangsrollerTekst = computed(() => muligeTilgangsrollerForMedlem()
      .filter(rolle => this.visningsmedlem().tilgangsroller.includes(rolle.rolle))
      .map(rolle => rolle.tittel(this.enumOversettelser))
      .join(', '));

    constructor(private avhengigheter: AdministrasjonMedlemAvhengigheter,
                private matDialog: MatDialog,
                private enumOversettelser: EnumOversettelser) {
    }

    async byttDetaljvisning() {
        this.viserDetaljer.update(vises => !vises);
        if (this.viserDetaljer() && this.gruppe() === null) await this.hentDetaljer();
    }

    kanGiTilgang(): boolean {
        const medlem = this.visningsmedlem();
        return !medlem.harTilgang && medlem.medlemstatus !== Medlemstatus.anonymisert;
    }

    invitasjonsstatusTekst(status: Invitasjonsstatus): string {
        switch (status) {
            case Invitasjonsstatus.ikke_sendt: return 'Ikke sendt';
            case Invitasjonsstatus.sendt: return 'Sendt';
            case Invitasjonsstatus.akseptert: return 'Godtatt';
            case Invitasjonsstatus.avslått: return 'Avslått';
        }
    }

    medlemsstatusTekst(status: Medlemstatus): string {
        switch (status) {
            case Medlemstatus.normal_tilgang: return 'Normal tilgang';
            case Medlemstatus.tilgang_stengt: return 'Tilgang stengt';
            case Medlemstatus.anonymisert: return 'Anonymisert';
        }
    }

    async stengTilgang() {
        const detaljertMedlem = this.detaljertMedlem();
        if (detaljertMedlem === null) return;

        const dialog = MedlemStengTilgangDialogKomponent.vis(this.matDialog, detaljertMedlem, detaljertMedlem.gruppe);
        if (await firstValueFrom(dialog.afterClosed()) === true) await this.oppdaterEtterTilgangsendring();
    }

    async gjenaapneTilgang() {
        const detaljertMedlem = this.detaljertMedlem();
        if (detaljertMedlem === null) return;

        const dialog = MedlemGjenåpneTilgangDialogKomponent.vis(this.matDialog, detaljertMedlem);
        if (await firstValueFrom(dialog.afterClosed()) === true) await this.oppdaterEtterTilgangsendring();
    }

    private async hentDetaljer() {
        const medlem = await this.avhengigheter.hent(this.visningsmedlem().id);
        this.visningsmedlem.set(medlem);
        this.gruppe.set(medlem.gruppe);
    }

    private async oppdaterEtterTilgangsendring() {
        await this.hentDetaljer();
        this.tilgangEndretFor.emit(this.visningsmedlem() as GruppeMedlemModell);
    }

    private detaljertMedlem(): GruppeMedlemModell | null {
        const medlem = this.visningsmedlem();
        return erDetaljertMedlem(medlem) ? medlem : null;
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonMedlemAvhengigheter {
    constructor(private ressurs: GruppeMedlemRessursClient) {
    }

    hent(id: MedlemId): Promise<GruppeMedlemModell> {
        return this.ressurs.hent(id).somPromise();
    }
}

function erDetaljertMedlem(medlem: Medlem): medlem is GruppeMedlemModell {
    return 'gruppe' in medlem;
}

function gruppeForMedlem(medlem: Medlem) {
    return erDetaljertMedlem(medlem) ? medlem.gruppe : null;
}
